import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, ArrowRight, Wand2 } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
interface NewDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const NewDocumentModal: React.FC<NewDocumentModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    addDocument
  } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const segments = useMemo(() => {
    if (!sourceText.trim()) return [];
    return sourceText.split(/\n\s*\n/).filter(text => text.trim().length > 0);
  }, [sourceText]);
  const handleSubmit = () => {
    if (!title.trim() || segments.length === 0) return;
    setIsSubmitting(true);
    // Simulate API delay for auto-translation
    setTimeout(() => {
      const docId = addDocument(title.trim(), sourceText);
      setIsSubmitting(false);
      onClose();
      toast.success(`Document created — translating ${segments.length} segments...`);
      navigate(`/document/${docId}`);
      // Reset form
      setTitle('');
      setSourceText('');
    }, 1500);
  };
  return <AnimatePresence data-id="element-175">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-176">
          
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-177">
            
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50" data-id="element-178">
                <div className="flex items-center gap-3" data-id="element-179">
                  <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-180">
                    <FileText className="w-5 h-5 text-indigo-600" data-id="element-181" />
                  </div>
                  <div data-id="element-182">
                    <h2 className="text-lg font-semibold text-slate-900" data-id="element-183">
                      New Translation Document
                    </h2>
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mt-0.5" data-id="element-184">
                      <span className="bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-185">
                        English
                      </span>
                      <ArrowRight className="w-3 h-3" data-id="element-186" />
                      <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded" data-id="element-187">
                        French
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={onClose} disabled={isSubmitting} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50" data-id="element-188">
                
                  <X className="w-5 h-5" data-id="element-189" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6" data-id="element-190">
                <div data-id="element-191">
                  <label htmlFor="doc-title" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-192">
                  
                    Document Title
                  </label>
                  <input id="doc-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Master Services Agreement - Acme Corp" className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" disabled={isSubmitting} data-id="element-193" />
                
                </div>

                <div className="flex-1 flex flex-col min-h-[300px]" data-id="element-194">
                  <label htmlFor="doc-source" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-195">
                  
                    English Source Text
                  </label>
                  <textarea id="doc-source" value={sourceText} onChange={e => setSourceText(e.target.value)} placeholder="Paste your English source text here...&#10;&#10;Separate sections with blank lines (double enter) to automatically create segments." className="flex-1 w-full p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm font-legal leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" disabled={isSubmitting} data-id="element-196" />
                
                </div>

                {/* Preview Section */}
                {segments.length > 0 && <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-4" data-id="element-197">
                    <div className="flex items-center justify-between mb-3" data-id="element-198">
                      <h3 className="text-sm font-medium text-indigo-900 flex items-center gap-2" data-id="element-199">
                        <Wand2 className="w-4 h-4 text-indigo-600" data-id="element-200" />
                        Segmentation Preview
                      </h3>
                      <span className="text-xs font-semibold px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full" data-id="element-201">
                        {segments.length} segments detected
                      </span>
                    </div>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-202">
                      {segments.map((seg, idx) => <div key={idx} className="text-xs text-slate-600 bg-white p-2.5 rounded border border-slate-200 shadow-sm line-clamp-2 font-legal" data-id="element-203">
                    
                          <span className="font-sans font-semibold text-slate-400 mr-2" data-id="element-204">
                            #{idx + 1}
                          </span>
                          {seg}
                        </div>)}
                    </div>
                  </div>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0" data-id="element-205">
                <button onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50" data-id="element-206">
                
                  Cancel
                </button>
                <button onClick={handleSubmit} disabled={!title.trim() || segments.length === 0 || isSubmitting} className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-207">
                
                  {isSubmitting ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-208" />
                      Translating...
                    </> : <>
                      Create & Translate
                      <ArrowRight className="w-4 h-4" data-id="element-209" />
                    </>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
};